import React, { useState, useMemo, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  useDomainRouterQuery,
  useLazyDomainRouterQuery,
} from "../../../state/domainRouterHooks";
import {
  useAddAdmissionsDocumentMutation,
  useGetSettingsModuleConfigQuery,
  useUpdateAdmissionsApplicationMutation,
  useUpsertEnrollmentStepDataMutation,
} from "../../../state/domainRouterApi";
import { uploadData, getUrl } from "aws-amplify/storage";
import { AppDialogFrame } from "../../../components/overlays/AppDialogFrame";
import { SupportErrorMessage } from "../../../components/overlays/EngineeringSupportBanner";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragOverEvent,
  DragEndEvent,
  useDroppable,
  defaultDropAnimationSideEffects,
  DropAnimation,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Search,
  X,
  ChevronDown,
  LayoutGrid,
  List as ListIcon,
  Plus,
  MoreHorizontal,
  CircleCheckBig,
  CircleX,
  Save,
  ExternalLink,
  FileText,
  Download,
  DollarSign,
  Clock,
  CheckSquare,
  UploadCloud,
  UserCircle,
  Eye,
  UserPlus,
  Edit2,
  Trash2,
  Calendar,
  ArrowRight,
  Archive,
  RotateCcw,
} from "lucide-react";
import { Applicant } from "../types";
import { useAdmissionsDashboardApplicants } from "../adapters/liveData";
import { resolveCognitoProfile } from "../../../utils/cognito-profile";
import { formatEventLabel } from "../utils/formatEventLabel";
import { useSchoolPublicInfo } from "../../school-settings-v2/hooks/useSchoolPublicInfo";
import {
  renderAcceptanceLetter,
  resolveAcceptanceLetterTemplateVariant,
  resolveChildGenderWords,
} from "../utils/acceptanceLetterTemplates";
import { buildAcceptanceLetterPdf } from "../utils/acceptanceLetterPdf";
import { SearchFilterControl } from "../../school-students-v2-1/components/ToolbarPrimitives";
import {
  FilterBarSection,
  FilterDropdownField,
  toSelectOptions,
} from "../../school-students-v2-1/components/FilterControls";
import styles from "./Dashboard.module.css";

type SortKey =
  | "name"
  | "studentsCount"
  | "grade"
  | "date"
  | "status"
  | "assigneeName"
  | "interviewDate"
  | "siblings"
  | null;
type SortDirection = "asc" | "desc";
const KANBAN_STATUSES = [
  "Applied",
  "In-Progress",
  "Scheduled",
  "Accepted",
  "Enrolled",
] as const;
type KanbanStatus = (typeof KANBAN_STATUSES)[number];
const BOARD_STATUS_DATA_IDS: Record<Applicant["status"], string> = {
  Applied: "submitted",
  "In-Progress": "under_review",
  Scheduled: "interview",
  Accepted: "accepted",
  Enrolled: "enrolled",
  Waitlisted: "waitlisted",
  Denied: "denied",
  Archived: "archived",
};
const KANBAN_COLUMN_MIN_WIDTH_PX = 220;
const kanbanColumnCount = KANBAN_STATUSES.length;
const KANBAN_GRID_TEMPLATE = `repeat(${kanbanColumnCount}, minmax(${KANBAN_COLUMN_MIN_WIDTH_PX}px, 1fr))`;
const ADMISSIONS_OPEN_SCHEDULE_KEY = "ystnj.admissions.openScheduleFor";

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asArray = <T,>(value: unknown): T[] =>
  Array.isArray(value) ? (value as T[]) : [];

const readText = (value: unknown): string => String(value ?? "").trim();

const parsePayloadRecord = (value: unknown): Record<string, unknown> => {
  if (typeof value === "string") {
    try {
      return asRecord(JSON.parse(value));
    } catch {
      return {};
    }
  }
  return asRecord(value);
};

const readFamilySelfSchedulingEnabled = (settingsPayload: unknown): boolean => {
  const admissionConfig = asRecord(asRecord(settingsPayload).admissionConfig);
  const interviewScheduling = asRecord(admissionConfig.interviewScheduling);
  return interviewScheduling.familySelfSchedulingEnabled === true;
};

const normalizeDashboardStatus = (
  status: Applicant["status"] | string,
  familySelfSchedulingEnabled: boolean,
): Applicant["status"] =>
  !familySelfSchedulingEnabled && status === "Scheduled"
    ? "In-Progress"
    : (status as Applicant["status"]);

const dashboardStatusMatches = (
  applicant: Applicant,
  status: string,
  familySelfSchedulingEnabled: boolean,
): boolean =>
  normalizeDashboardStatus(applicant.status, familySelfSchedulingEnabled) ===
  status;

const dashboardRawStatusMatches = (
  applicant: Applicant,
  status: string,
): boolean => applicant.status === status;

const getBoardStatusColor = (status: KanbanStatus): string => {
  if (status === "Applied") return "aquamarine";
  if (status === "In-Progress") return "primary";
  if (status === "Scheduled") return "vibrant-coral";
  if (status === "Accepted") return "jungle";
  return "primary";
};

const unwrapMutationResult = async (
  mutationResult: unknown,
): Promise<unknown> => {
  if (
    mutationResult &&
    typeof mutationResult === "object" &&
    typeof (mutationResult as { unwrap?: unknown }).unwrap === "function"
  ) {
    return (mutationResult as { unwrap: () => Promise<unknown> }).unwrap();
  }
  const result = await mutationResult;
  const resultRecord = asRecord(result);
  if ("error" in resultRecord) {
    throw resultRecord.error;
  }
  if ("data" in resultRecord) {
    return resultRecord.data;
  }
  return result;
};

const readErrorMessage = (error: unknown, fallback: string): string => {
  const direct = readText((error as { message?: unknown } | null)?.message);
  if (direct) return direct;
  const nested = readText(
    asRecord((error as { data?: unknown } | null)?.data).message,
  );
  return nested || fallback;
};

const asUnixSeconds = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value))
    return Math.trunc(value);
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
};

const formatLetterDate = (): string =>
  new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const buildDefaultSchoolYear = (
  referenceEpochSeconds?: number | null,
): string => {
  const referenceDate =
    typeof referenceEpochSeconds === "number" && referenceEpochSeconds > 0
      ? new Date(referenceEpochSeconds * 1000)
      : new Date();
  const year = referenceDate.getFullYear();
  return `${year}-${year + 1}`;
};

const toSafeToken = (value: string): string =>
  String(value ?? "")
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 120);

const buildAcceptanceLetterStoragePath = (input: {
  applicationId: string;
  childApplicationId: string;
  fileStem: string;
  childName: string;
}): string => {
  const now = Date.now();
  const safeStem = toSafeToken(input.fileStem) || "acceptance-letter";
  const safeChildName = toSafeToken(input.childName) || "student";
  return `uploads/admissions/${input.applicationId}/system/acceptance-letter/${input.childApplicationId}/${now}-${safeStem}-${safeChildName}.pdf`;
};

const getInitials = (value: string): string => {
  const cleaned = String(value ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (cleaned.length === 0) return "??";
  if (cleaned.length === 1) return cleaned[0].slice(0, 2).toUpperCase();
  return `${cleaned[0][0]}${cleaned[cleaned.length - 1][0]}`.toUpperCase();
};

const normalizeGradeLabel = (value: unknown): string => {
  const raw = String(value ?? "").trim();
  if (!raw) return "Grade K";

  const cleaned = raw.replace(/[_-]+/g, " ").trim();
  const token = cleaned.replace(/^grade\s*/i, "").trim();
  if (!token) return "Grade K";
  if (/^k(?:indergarten)?$/i.test(token)) return "Grade K";
  if (/^\d+$/.test(token)) return `Grade ${String(Number(token))}`;
  if (/^[a-z]$/i.test(token)) return `Grade ${token.toUpperCase()}`;
  return `Grade ${token.replace(/\b\w/g, (char) => char.toUpperCase())}`;
};

const getBoardStatusDataId = (status: string): string =>
  BOARD_STATUS_DATA_IDS[status as Applicant["status"]] ??
  String(status ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_");

// --- Sortable Card Component ---
interface SortableApplicantCardProps {
  applicant: Applicant;
  onClick: (applicant: Applicant) => void;
  onSendOffer: (applicant: Applicant, event?: React.MouseEvent) => void;
  isSendingOffer: boolean;
  familySelfSchedulingEnabled: boolean;
  clickable?: boolean;
  dragEnabled?: boolean;
}

const SortableApplicantCard: React.FC<SortableApplicantCardProps> = ({
  applicant,
  onClick,
  onSendOffer,
  isSendingOffer,
  familySelfSchedulingEnabled,
  clickable = true,
  dragEnabled = true,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: applicant.id,
    data: {
      type: "Applicant",
      applicant,
    },
    disabled: !dragEnabled,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };
  const boardStatus = getBoardStatusDataId(applicant.status);
  const interactionClassName = clickable
    ? dragEnabled
      ? "cursor-pointer md:cursor-grab"
      : "cursor-pointer"
    : "cursor-default pointer-events-none";

  const interviewDate = applicant.interviewDate
    ? new Date(applicant.interviewDate)
    : null;
  const interviewMonth =
    interviewDate && !Number.isNaN(interviewDate.getTime())
      ? interviewDate.toLocaleDateString("en-US", { month: "short" })
      : "";
  const interviewDay =
    interviewDate && !Number.isNaN(interviewDate.getTime())
      ? String(interviewDate.getDate())
      : "";

  return (
    <article
      ref={setNodeRef}
      style={style}
      {...(dragEnabled ? attributes : {})}
      {...(dragEnabled ? listeners : {})}
      data-testid={clickable ? "board-card" : undefined}
      data-test-id={clickable ? "board-card" : undefined}
      data-application-id={clickable ? applicant.id : undefined}
      data-reference-code={
        clickable ? (applicant.applicationCode ?? undefined) : undefined
      }
      data-status={clickable ? boardStatus : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => onClick(applicant) : undefined}
      onKeyDown={
        clickable
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onClick(applicant);
              }
            }
          : undefined
      }
      className={`bg-white p-4 rounded-xl shadow-card hover:shadow-soft transition-shadow ${interactionClassName} ${styles.sortableApplicantCard} group border border-transparent hover:border-primary/20 mb-3 relative`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          {applicant.avatar ? (
            <img
              alt="Student"
              className="w-10 h-10 rounded-full object-cover bg-gray-100"
              src={applicant.avatar}
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">
              {getInitials(applicant.name)}
            </div>
          )}
          <div>
            <h4 className="font-semibold text-sm text-irongrey group-hover:text-primary transition-colors">
              {applicant.name}
            </h4>
            <p className="hidden">
              {applicant.division && <span>{applicant.division}. </span>}
              {applicant.grade}
            </p>
          </div>
        </div>
        {applicant.status === "Enrolled" && (
          <CircleCheckBig
            width="18"
            height="18"
            className="lucide lucide-circle-check-big text-aquamarine"
          />
        )}
        {applicant.status === "Denied" && (
          <CircleX
            width="18"
            height="18"
            className="lucide lucide-circle-x text-vibrant-coral"
          />
        )}
      </div>

      {applicant.progress !== undefined && applicant.status !== "Enrolled" && (
        <div className="mt-2 w-full bg-gray-50 rounded-full h-1.5 mb-2 overflow-hidden">
          <div
            className="bg-primary h-1.5 rounded-full"
            style={{ width: `${applicant.progress}%` }}
          ></div>
        </div>
      )}

      {familySelfSchedulingEnabled && applicant.interviewDate && (
        <div className="bg-vibrant-coral/5 rounded-lg p-2.5 flex items-center gap-3 mb-2 border border-vibrant-coral/10">
          <div className="bg-white p-1.5 rounded-md shadow-sm text-center min-w-[40px] border border-gray-100">
            <span className="block text-[8px] uppercase font-bold text-vibrant-coral">
              {interviewMonth || "--"}
            </span>
            <span className="block text-sm font-bold text-irongrey">
              {interviewDay || "--"}
            </span>
          </div>
          <div>
            <p className="text-xs font-bold text-irongrey">Family Interview</p>
            <p className="text-[10px] text-irongrey/50">
              {applicant.interviewRoom}
            </p>
          </div>
        </div>
      )}
      {applicant.status === "Accepted" ? (
        <button
          type="button"
          onClick={(event) => {
            if (!clickable) return;
            onSendOffer(applicant, event);
          }}
          disabled={!clickable || isSendingOffer}
          className="mt-2 w-full cursor-pointer text-xs font-bold text-white bg-jungle px-3 py-2 rounded-lg hover:bg-jungle/90 transition-all disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSendingOffer ? "Generating Letter..." : "Acceptance Letter"}
        </button>
      ) : null}

      <div className="flex items-center justify-between mt-2">
        {applicant.status === "Enrolled" ? (
          <>
            <span className="text-[10px] text-jungle font-bold uppercase tracking-wide">
              Enrolled
            </span>
            <button
              type="button"
              disabled={!clickable}
              onClick={(event) => {
                if (!clickable) return;
                event.stopPropagation();
                onClick(applicant);
              }}
              className="text-[10px] cursor-pointer bg-primary text-white px-2.5 py-1 rounded-lg transition-colors font-bold shadow-sm shadow-primary/10 disabled:cursor-not-allowed disabled:opacity-60"
            >
              View Profile
            </button>
          </>
        ) : applicant.status === "Denied" ? (
          <>
            <span className="text-[10px] text-vibrant-coral font-bold uppercase tracking-wide">
              Denied
            </span>
            <span className="text-[10px] text-irongrey/40">Archived</span>
          </>
        ) : (
          <>
            <span
              className={`text-[10px] font-medium ${applicant.statusLabel === "Urgent Priority" ? "text-vibrant-coral font-bold" : "text-irongrey/40"}`}
            >
              {applicant.statusLabel}
            </span>
            {applicant.progress !== undefined ? (
              <p className="text-[10px] text-irongrey/60 font-bold">
                {applicant.progress}%
              </p>
            ) : (
              <p className="text-[10px] text-irongrey/40">{applicant.date}</p>
            )}
          </>
        )}
      </div>
    </article>
  );
};

// --- Kanban Column Component ---
const KanbanColumn: React.FC<{
  id: string;
  title: string;
  color: string;
  count: number;
  applicants: Applicant[];
  onCardClick: (applicant: Applicant) => void;
  onAddApplicant: () => void;
  onArchiveAll: () => void;
  onSortByDate: () => void;
  onMoveAll: (newStatus: Applicant["status"]) => void;
  onRenameTitle: (newTitle: string) => void;
  onSendOffer: (applicant: Applicant, event?: React.MouseEvent) => void;
  sendingOfferId: string | null;
  dragEnabled: boolean;
  familySelfSchedulingEnabled: boolean;
}> = ({
  id,
  title,
  color,
  count,
  applicants,
  onCardClick,
  onAddApplicant,
  onArchiveAll,
  onSortByDate,
  onMoveAll,
  onRenameTitle,
  onSendOffer,
  sendingOfferId,
  dragEnabled,
  familySelfSchedulingEnabled,
}) => {
  const { setNodeRef } = useDroppable({
    id: id,
  });

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuStep, setMenuStep] = useState<"main" | "moveAll">("main");
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState(title);
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
        setMenuStep("main");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isEditingTitle && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditingTitle]);

  const handleTitleSubmit = (e: React.FormEvent | React.KeyboardEvent) => {
    if (tempTitle.trim()) {
      onRenameTitle(tempTitle);
      setIsEditingTitle(false);
    }
  };

  const statusOptions: Applicant["status"][] = [
    "Applied",
    "In-Progress",
    "Scheduled",
    "Accepted",
    "Enrolled",
  ].filter(
    (status) => familySelfSchedulingEnabled || status !== "Scheduled",
  ) as Applicant["status"][];

  return (
    <section
      data-testid="board-column"
      data-test-id="board-column"
      data-status={getBoardStatusDataId(id)}
      className="w-full flex flex-col h-full min-w-0"
      aria-label={`${title} column`}
    >
      <header className="flex items-center justify-between mb-4 px-1 flex-shrink-0 relative">
        <div className="flex items-center gap-2 overflow-hidden flex-1 mr-2">
          <span
            className={`w-2 h-2 rounded-full bg-${color} flex-shrink-0`}
          ></span>
          {isEditingTitle ? (
            <input
              ref={inputRef}
              className="text-sm font-bold text-irongrey bg-gray-50 border border-primary/30 rounded px-2 py-0.5 focus:ring-1 focus:ring-primary w-full"
              value={tempTitle}
              onChange={(e) => setTempTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleTitleSubmit(e);
                if (e.key === "Escape") setIsEditingTitle(false);
              }}
              onBlur={handleTitleSubmit}
            />
          ) : (
            <h3 className="font-bold text-irongrey truncate">{title}</h3>
          )}
          <span className="text-xs font-bold text-irongrey/50 bg-gray-100 px-2 py-0.5 rounded-md flex-shrink-0">
            {count}
          </span>
        </div>
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => {
              setIsMenuOpen(!isMenuOpen);
              setMenuStep("main");
            }}
            className={`text-irongrey/40 cursor-pointer hover:text-primary transition-colors p-1 rounded-md hover:bg-gray-100 ${isMenuOpen ? "text-primary bg-primary/5" : ""}`}
          >
            <MoreHorizontal size={20} />
          </button>

          {isMenuOpen && (
            <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)] border border-gray-100 py-1.5 z-50 animate-in fade-in zoom-in-95 duration-100 origin-top-right">
              {menuStep === "main" ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setIsMenuOpen(false);
                      setIsEditingTitle(true);
                    }}
                    className="w-full text-left px-4 py-2.5 text-xs font-bold text-irongrey hover:bg-gray-50 flex items-center gap-3 transition-colors"
                  >
                    <Edit2 size={14} className="text-irongrey/60" />
                    Edit Column
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsMenuOpen(false);
                      onAddApplicant();
                    }}
                    className="w-full text-left px-4 py-2.5 text-xs font-bold text-irongrey hover:bg-gray-50 flex items-center gap-3 transition-colors"
                  >
                    <Plus size={14} className="text-irongrey/60" />
                    Add Applicant
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      onSortByDate();
                      setIsMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-xs font-bold text-irongrey hover:bg-gray-50 flex items-center gap-3 transition-colors"
                  >
                    <Calendar size={14} className="text-irongrey/60" />
                    Sort by Date
                  </button>
                  <button
                    type="button"
                    onClick={() => setMenuStep("moveAll")}
                    className="w-full text-left px-4 py-2.5 text-xs font-bold text-irongrey hover:bg-gray-50 flex items-center gap-3 transition-colors"
                  >
                    <ArrowRight size={14} className="text-irongrey/60" />
                    Move All Cards
                  </button>
                  <div className="h-px bg-gray-50 my-1"></div>
                  <button
                    type="button"
                    onClick={() => {
                      onArchiveAll();
                      setIsMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-xs font-bold text-strawberry hover:bg-strawberry/5 flex items-center gap-3 transition-colors"
                  >
                    <Archive size={14} />
                    Archive All
                  </button>
                </>
              ) : (
                <>
                  <div className="px-4 py-2 border-b border-gray-50 mb-1 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setMenuStep("main")}
                      className="text-irongrey/40 cursor-pointer hover:text-primary"
                    >
                      <ChevronDown size={14} className="rotate-90" />
                    </button>
                    <span className="text-[10px] font-bold text-irongrey/40 uppercase tracking-widest">
                      Move All to...
                    </span>
                  </div>
                  {statusOptions.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => {
                        onMoveAll(opt);
                        setIsMenuOpen(false);
                        setMenuStep("main");
                      }}
                      className="w-full text-left px-4 py-2 text-xs font-medium hover:bg-gray-50 text-irongrey"
                    >
                      {opt}
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </header>

      <section
        ref={setNodeRef}
        className="flex-1 overflow-y-auto pr-2 pb-4 min-h-[150px]"
      >
        <SortableContext
          items={applicants.map((a) => a.id)}
          strategy={verticalListSortingStrategy}
        >
          {applicants.map((applicant) => (
            <SortableApplicantCard
              key={applicant.id}
              applicant={applicant}
              onClick={onCardClick}
              onSendOffer={onSendOffer}
              isSendingOffer={sendingOfferId === applicant.id}
              dragEnabled={dragEnabled}
              familySelfSchedulingEnabled={familySelfSchedulingEnabled}
            />
          ))}
        </SortableContext>
      </section>
    </section>
  );
};

// --- Utils & Subcomponents ---

const AddApplicantModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({
  isOpen,
  onClose,
}) => {
  if (!isOpen) return null;
  const [code, setCode] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [triggerLookup, lookupState] =
    useLazyDomainRouterQuery<"lookupApplication">();
  const navigate = useNavigate();

  const submitLookup = async () => {
    const trimmedCode = code.trim();
    const trimmedFamilyName = familyName.trim();
    if (!trimmedCode || !trimmedFamilyName) {
      return;
    }
    try {
      const result = await triggerLookup("lookupApplication", {
        code: trimmedCode,
        familyName: trimmedFamilyName,
      });
      if (!result?.found || !result.application?.id) {
        return;
      }
      onClose();
      navigate(`/school/admissions/children/${result.application.id}`);
    } catch {
      // UI below surfaces a user-safe failure message.
    }
  };

  return (
    <AppDialogFrame
      open={isOpen}
      onClose={onClose}
      title="Lookup Application"
      description="Search by application code and family name."
      testId="admissions-lookup-application-modal"
      maxWidthClassName="max-w-lg"
      titleClassName="text-xl"
      descriptionClassName="text-xs font-medium text-irongrey/40"
      bodyClassName="space-y-6"
      bodyPaddingClassName="p-8"
      footerPaddingClassName="px-8 py-5"
      footer={
        <>
          <button
            onClick={onClose}
            className="px-6 py-2 text-sm font-bold text-irongrey/60 transition-colors hover:text-irongrey"
          >
            Cancel
          </button>
          <button
            onClick={() => void submitLookup()}
            disabled={
              lookupState.isLoading ||
              lookupState.isFetching ||
              code.trim().length === 0 ||
              familyName.trim().length === 0
            }
            className="flex items-center gap-2 rounded-xl bg-primary px-8 py-2 text-sm font-bold text-white shadow-lg shadow-primary/20 transition-all hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Search size={16} />
            Search
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4">
        <div>
          <label className="text-[10px] font-bold text-irongrey/40 uppercase tracking-widest mb-2 block">
            Application Code
          </label>
          <input
            type="text"
            className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-sm font-semibold text-irongrey focus:ring-primary focus:border-primary placeholder:text-irongrey/30"
            placeholder="e.g. YST-2026-00012"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void submitLookup();
            }}
          />
        </div>
        <div>
          <label className="text-[10px] font-bold text-irongrey/40 uppercase tracking-widest mb-2 block">
            Family Name
          </label>
          <input
            type="text"
            className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-sm font-semibold text-irongrey focus:ring-primary focus:border-primary placeholder:text-irongrey/30"
            placeholder="e.g. Cohen"
            value={familyName}
            onChange={(e) => setFamilyName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void submitLookup();
            }}
          />
        </div>
      </div>
      {lookupState.isFetching || lookupState.isLoading ? (
        <p className="text-xs font-semibold text-irongrey/50">Searching…</p>
      ) : null}
      {lookupState.error ? (
        <p className="text-xs font-semibold text-strawberry">
          Unable to lookup this application right now.
        </p>
      ) : null}
      {lookupState.data && !lookupState.data.found ? (
        <p className="text-xs font-semibold text-irongrey/50">
          Application lookup returned no match.
        </p>
      ) : null}
    </AppDialogFrame>
  );
};

interface HeaderCellProps {
  id: string;
  width: number;
  label: string;
  onResize: (id: string, e: React.MouseEvent) => void;
  onClick?: () => void;
  center?: boolean;
  right?: boolean;
}

const HeaderCell: React.FC<HeaderCellProps> = ({
  id,
  width,
  label,
  onResize,
  onClick,
  center,
  right,
}) => (
  <th
    className={`px-2 py-4 text-[10px] font-bold text-irongrey/40 uppercase tracking-widest relative group select-none bg-gray-50 ${center ? "text-center" : right ? "text-right" : "text-left"}`}
    style={{ width }}
  >
    <button
      type="button"
      className={`w-full truncate bg-transparent p-0 text-left ${right ? "pl-4" : "pr-4"} ${onClick ? "hover:text-irongrey" : ""}`}
      disabled={!onClick}
      onClick={onClick}
    >
      {label}
    </button>
    <div
      className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/50 opacity-0 group-hover:opacity-100 z-10"
      onMouseDown={(e) => onResize(id, e)}
    />
  </th>
);

const StatusBadge: React.FC<{
  status: string;
  familySelfSchedulingEnabled?: boolean;
}> = ({ status, familySelfSchedulingEnabled = true }) => {
  const displayStatus = normalizeDashboardStatus(
    status,
    familySelfSchedulingEnabled,
  );
  let colorClass = "bg-gray-100 text-irongrey/60";

  switch (displayStatus) {
    case "Applied":
      colorClass = "bg-aquamarine/10 text-aquamarine";
      break;
    case "In-Progress":
      colorClass = "bg-primary/10 text-primary";
      break;
    case "Scheduled":
      colorClass = "bg-vibrant-coral/10 text-vibrant-coral";
      break;
    case "Accepted":
      colorClass = "bg-jungle/10 text-jungle";
      break;
    case "Enrolled":
      colorClass = "bg-primary text-white";
      break;
    case "Waitlisted":
      colorClass = "bg-amber-100 text-amber-600";
      break;
    case "Denied":
      colorClass = "bg-vibrant-coral text-white";
      break;
    case "Archived":
      colorClass = "bg-gray-500 text-white";
      break;
  }

  return (
    <span
      className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide ${colorClass}`}
    >
      {displayStatus}
    </span>
  );
};

const SkeletonBlock: React.FC<{ className?: string }> = ({ className }) => (
  <span
    aria-hidden="true"
    className={`block animate-pulse rounded-md bg-gray-100 ${className ?? ""}`}
  />
);

const AdmissionsApplicantCardSkeleton: React.FC = () => (
  <article
    aria-busy="true"
    className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm"
  >
    <header className="flex items-start justify-between gap-3">
      <section className="flex items-center gap-3">
        <SkeletonBlock className="h-10 w-10 rounded-full" />
        <section className="space-y-2">
          <SkeletonBlock className="h-3 w-28" />
          <SkeletonBlock className="h-2.5 w-16" />
        </section>
      </section>
      <SkeletonBlock className="h-4 w-4 rounded-full" />
    </header>
    <section className="mt-3">
      <SkeletonBlock className="h-1.5 w-full rounded-full" />
    </section>
    <footer className="mt-4 space-y-2">
      <SkeletonBlock className="h-8 w-full rounded-lg" />
      <section className="flex items-center justify-between">
        <SkeletonBlock className="h-2.5 w-16" />
        <SkeletonBlock className="h-2.5 w-12" />
      </section>
    </footer>
  </article>
);

const AdmissionsBoardSkeleton: React.FC<{
  statuses: readonly KanbanStatus[];
}> = ({ statuses }) => {
  const visibleColumns = Math.max(1, statuses.length || 1);
  return (
    <section
      data-testid="admissions-dashboard-board-skeleton"
      data-column-count={visibleColumns}
      className="h-full min-h-0 overflow-auto pb-10"
      aria-label="Admissions board loading skeleton"
      aria-busy="true"
    >
      <section className="min-w-full">
        <section
          className="grid items-start gap-2"
          style={{ gridTemplateColumns: KANBAN_GRID_TEMPLATE }}
        >
          {statuses.map((status) => (
            <section
              key={`skeleton-column-${status}`}
              className="flex min-w-0 flex-col"
            >
              <header className="relative mb-4 flex items-center justify-between px-1">
                <section className="flex items-center gap-2 overflow-hidden">
                  <SkeletonBlock className="h-2 w-2 rounded-full" />
                  <h3 className="truncate text-sm font-bold text-irongrey/35">
                    {status}
                  </h3>
                </section>
                <SkeletonBlock className="h-5 w-8 rounded-md" />
              </header>
              <ul className="space-y-3 pr-2" aria-hidden="true">
                <li>
                  <AdmissionsApplicantCardSkeleton />
                </li>
                <li>
                  <AdmissionsApplicantCardSkeleton />
                </li>
              </ul>
            </section>
          ))}
        </section>
      </section>
    </section>
  );
};

const AdmissionsListSkeleton: React.FC = () => (
  <section
    data-testid="admissions-dashboard-list-skeleton"
    className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm"
    aria-label="Admissions list loading skeleton"
    aria-busy="true"
  >
    <section className="flex-1 overflow-auto pb-24">
      <table className="table-sticky-last-action-col w-full table-auto border-collapse text-left">
        <thead className="sticky top-0 bg-gray-50">
          <tr className="border-b border-gray-100 bg-gray-50 shadow-[0_1px_0_0_rgba(229,231,235,1)]">
            <th className="px-2 py-4 text-left text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
              Family Name
            </th>
            <th className="px-2 py-4 text-center text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
              Students
            </th>
            <th className="px-2 py-4 text-left text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
              Siblings
            </th>
            <th className="px-2 py-4 text-left text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
              Grades
            </th>
            <th className="px-2 py-4 text-left text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
              Submitted
            </th>
            <th className="px-2 py-4 text-left text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
              Status
            </th>
            <th className="px-2 py-4 text-left text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
              Assignee
            </th>
            <th className="px-2 py-4 text-left text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
              Interview
            </th>
            <th className="px-2 py-4 text-right text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {Array.from({ length: 8 }).map((_, index) => (
            <tr key={`admissions-list-skeleton-row-${index}`} className="h-16">
              <td className="px-2 py-4">
                <section className="flex items-center gap-3">
                  <SkeletonBlock className="h-8 w-8 rounded-full" />
                  <section className="space-y-2">
                    <SkeletonBlock className="h-3 w-28" />
                    <SkeletonBlock className="h-2.5 w-36" />
                  </section>
                </section>
              </td>
              <td className="px-2 py-4 text-center">
                <SkeletonBlock className="mx-auto h-5 w-8 rounded" />
              </td>
              <td className="px-2 py-4">
                <SkeletonBlock className="h-2.5 w-16" />
              </td>
              <td className="px-2 py-4">
                <SkeletonBlock className="h-2.5 w-14" />
              </td>
              <td className="px-2 py-4">
                <SkeletonBlock className="h-2.5 w-20" />
              </td>
              <td className="px-2 py-4">
                <SkeletonBlock className="h-5 w-20 rounded-full" />
              </td>
              <td className="px-2 py-4">
                <SkeletonBlock className="h-2.5 w-24" />
              </td>
              <td className="px-2 py-4">
                <SkeletonBlock className="h-2.5 w-24" />
              </td>
              <td className="px-2 py-4 text-right">
                <SkeletonBlock className="ml-auto h-8 w-20 rounded-lg" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  </section>
);

const QuickViewPanel: React.FC<{
  applicant: Applicant | null;
  familySelfSchedulingEnabled: boolean;
  onClose: () => void;
  onViewFull: () => void;
}> = ({ applicant, familySelfSchedulingEnabled, onClose, onViewFull }) => {
  if (!applicant) return null;

  const quickView = useDomainRouterQuery(
    "getApplicationQuickView",
    { applicationId: applicant.id },
    { skip: !applicant.id },
  );
  const quickViewData = quickView.data;
  const primaryStudent = quickViewData?.students?.[0];

  const docsPreview = (quickViewData?.documents ?? [])
    .flatMap((doc) => (doc ? [doc] : []))
    .slice(0, 2);
  const timelinePreview = (quickViewData?.timeline ?? [])
    .flatMap((entry) => (entry ? [entry] : []))
    .slice(0, 3);

  const formatEpochSeconds = (value: unknown): string => {
    const seconds = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(seconds) || seconds <= 0) return "";
    try {
      return new Date(seconds * 1000).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "";
    }
  };

  const familyLabel = quickViewData?.familyName
    ? `${quickViewData.familyName} Family`
    : `${applicant.name.split(" ")[1] ?? applicant.name} Family`;
  const referenceCode = quickViewData?.referenceCode ?? "";

  return createPortal(
    <>
      <div
        data-passive-click-surface="true"
        className="fixed inset-0 z-[100] bg-irongrey/20 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div className="fixed top-0 right-0 bottom-0 z-[101] w-full max-w-md bg-white shadow-2xl animate-in slide-in-from-right duration-300 flex flex-col">
        <div className="px-6 py-6 border-b border-gray-100 flex items-start justify-between bg-white shrink-0">
          <div>
            <h2 className="text-xl font-bold text-irongrey leading-tight">
              {familyLabel}
            </h2>
            {referenceCode ? (
              <p className="text-[11px] font-medium text-irongrey/40 mt-1 uppercase tracking-wide">
                Ref: {referenceCode}
              </p>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onViewFull}
              className="text-xs font-bold text-irongrey border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors flex items-center gap-1.5"
            >
              View Full Profile
              <ExternalLink size={12} />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-irongrey/40 hover:bg-gray-100 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <UserCircle size={18} className="text-irongrey/40" />
              <h3 className="text-xs font-bold text-irongrey/40 uppercase tracking-widest">
                Student Information
              </h3>
            </div>
            <div className="bg-background-light border border-gray-100 rounded-2xl p-5">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h4 className="text-lg font-bold text-irongrey">
                    {primaryStudent
                      ? `${primaryStudent.firstName} ${primaryStudent.lastName}`
                      : applicant.name}
                  </h4>
                </div>
                <StatusBadge
                  status={applicant.status}
                  familySelfSchedulingEnabled={familySelfSchedulingEnabled}
                />
              </div>

              <div className="grid grid-cols-2 gap-y-4">
                <div>
                  <p className="text-[10px] font-bold text-irongrey/40 uppercase mb-0.5">
                    Grade Applying
                  </p>
                  <p className="text-sm font-semibold text-irongrey">
                    {normalizeGradeLabel(
                      primaryStudent?.gradeApplying ?? applicant.grade,
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-irongrey/40 uppercase mb-0.5">
                    Application ID
                  </p>
                  <p className="text-sm font-semibold text-irongrey">
                    #{applicant.id}
                  </p>
                </div>
                {primaryStudent?.dateOfBirth ? (
                  <div className="col-span-2">
                    <p className="text-[10px] font-bold text-irongrey/40 uppercase mb-0.5">
                      DOB
                    </p>
                    <p className="text-sm font-semibold text-irongrey">
                      {formatEpochSeconds(primaryStudent.dateOfBirth)}
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3">
              <FileText size={18} className="text-irongrey/40" />
              <h3 className="text-xs font-bold text-irongrey/40 uppercase tracking-widest">
                Documents
              </h3>
            </div>
            <div className="space-y-3">
              {quickView.isLoading || quickView.isFetching ? (
                <div className="p-4 rounded-xl border border-gray-100 bg-white text-sm text-irongrey/60">
                  Loading documents…
                </div>
              ) : null}
              {!quickView.isLoading &&
              !quickView.isFetching &&
              docsPreview.length === 0 ? (
                <div className="p-4 rounded-xl border border-gray-100 bg-white text-sm text-irongrey/60">
                  Document list is syncing.
                </div>
              ) : null}
              {docsPreview.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-3 border border-gray-100 rounded-xl hover:border-primary/20 transition-colors group bg-white"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-strawberry/10 flex items-center justify-center text-strawberry">
                      <FileText size={16} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-irongrey">
                        {String(doc.category ?? "Document")}
                      </p>
                      <p className="text-[10px] text-irongrey/40 uppercase font-bold">
                        {formatEpochSeconds(doc.uploadedAt)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3">
              <Clock size={18} className="text-irongrey/40" />
              <h3 className="text-xs font-bold text-irongrey/40 uppercase tracking-widest">
                Application Timeline
              </h3>
            </div>
            <div className="ml-2 pl-4 border-l-2 border-gray-100 space-y-6 py-1">
              <div className="relative">
                <div className="absolute -left-[21px] top-0 w-3 h-3 rounded-full bg-primary ring-4 ring-white shadow-sm"></div>
                <h4 className="text-sm font-bold text-irongrey">
                  Status: {applicant.status}
                </h4>
                <p className="text-xs text-irongrey/40 mt-0.5">
                  {applicant.date || ""}
                </p>
              </div>
              {quickView.isLoading || quickView.isFetching ? (
                <div className="text-sm text-irongrey/60">
                  Loading timeline…
                </div>
              ) : null}
              {!quickView.isLoading &&
              !quickView.isFetching &&
              timelinePreview.length === 0 ? (
                <div className="text-sm text-irongrey/60">
                  No recent activity.
                </div>
              ) : null}
              {timelinePreview.map((entry, index) => {
                const rawTitle = String(
                  entry.eventType ?? entry.summary ?? `Event ${index + 1}`,
                );
                const normalizedTitle = formatEventLabel(rawTitle);
                const normalizedSummary = formatEventLabel(
                  String(entry.summary ?? entry.eventType ?? ""),
                );
                return (
                  <div
                    key={entry.id ?? `timeline-preview-${index}`}
                    className="relative"
                  >
                    <div className="absolute -left-[21px] top-0 w-3 h-3 rounded-full bg-gray-200 ring-4 ring-white shadow-sm"></div>
                    <h4 className="text-sm font-bold text-irongrey">
                      {normalizedTitle}
                    </h4>
                    {normalizedSummary &&
                    normalizedSummary !== normalizedTitle ? (
                      <p className="text-xs text-irongrey/60 mt-0.5">
                        {normalizedSummary}
                      </p>
                    ) : null}
                    <p className="text-xs text-irongrey/40 mt-0.5">
                      {formatEpochSeconds(entry.occurredAt)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

export const Dashboard: React.FC = () => {
  const LIST_PAGE_SIZE = 25;
  // --- State ---
  const { query: admissionsQuery, applicants } =
    useAdmissionsDashboardApplicants(200);
  const schoolPublicInfo = useSchoolPublicInfo();
  const settingsQuery = useGetSettingsModuleConfigQuery({
    moduleKey: "staff_settings",
  });
  const familySelfSchedulingEnabled = useMemo(
    () =>
      readFamilySelfSchedulingEnabled(
        parsePayloadRecord(asRecord(settingsQuery.data).payload),
      ),
    [settingsQuery.data],
  );
  const isInitialLoading =
    ((admissionsQuery.isLoading || admissionsQuery.isFetching) &&
      !admissionsQuery.data) ||
    ((settingsQuery.isLoading || settingsQuery.isFetching) &&
      !settingsQuery.data);
  const hasInitialLoadError =
    Boolean(admissionsQuery.error) && !admissionsQuery.data;

  const [viewMode, setViewMode] = useState<"grid" | "list">(() => {
    return (
      (sessionStorage.getItem("dashboardViewMode") as "grid" | "list") || "list"
    );
  });

  // Persist viewMode
  useEffect(() => {
    sessionStorage.setItem("dashboardViewMode", viewMode);
  }, [viewMode]);

  const [items, setItems] = useState<Applicant[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [dashboardSurfaceWidth, setDashboardSurfaceWidth] = useState(0);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const dashboardRootRef = useRef<HTMLElement | null>(null);
  const visibleKanbanStatuses = KANBAN_STATUSES;
  const isMobile =
    dashboardSurfaceWidth > 0 ? dashboardSurfaceWidth < 1024 : false;
  const dragEnabled = !isMobile;

  useEffect(() => {
    const root = dashboardRootRef.current;
    if (!root) {
      return () => {};
    }

    const syncWidth = () => {
      setDashboardSurfaceWidth(Math.round(root.getBoundingClientRect().width));
    };

    syncWidth();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(syncWidth);
      observer.observe(root);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", syncWidth);
    return () => window.removeEventListener("resize", syncWidth);
  }, []);

  useEffect(() => {
    // Sync the board items with the latest domain-router data.
    setItems(applicants);
  }, [applicants]);

  // Quick View & Menu State
  const [quickViewApplicant, setQuickViewApplicant] =
    useState<Applicant | null>(null);
  const [actionMenuState, setActionMenuState] = useState<{
    id: string;
    top: number;
    left: number;
  } | null>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);
  const [updateApplicationMutation] = useUpdateAdmissionsApplicationMutation();
  const [financialAidTogglePendingId, setFinancialAidTogglePendingId] =
    useState<string | null>(null);
  const [financialAidToggleErrors, setFinancialAidToggleErrors] = useState<
    Record<string, string>
  >({});

  // Filters Defaults
  const DEFAULT_FILTERS = {
    students: "All",
    grade: "All",
    status: "All Status",
    assignee: "All Staff",
  };

  // Filters State
  const [studentsFilter, setStudentsFilter] = useState(
    DEFAULT_FILTERS.students,
  );
  const [gradeFilter, setGradeFilter] = useState(DEFAULT_FILTERS.grade);
  const [statusFilter, setStatusFilter] = useState(DEFAULT_FILTERS.status);
  const [assigneeFilter, setAssigneeFilter] = useState(
    DEFAULT_FILTERS.assignee,
  );

  useEffect(() => {
    if (!familySelfSchedulingEnabled && statusFilter === "Scheduled") {
      setStatusFilter(DEFAULT_FILTERS.status);
    }
  }, [familySelfSchedulingEnabled, statusFilter, DEFAULT_FILTERS.status]);

  const assigneeOptions = useMemo(() => {
    const names = Array.from(
      new Set(
        items.map((item) => item.assigneeName).filter(Boolean) as string[],
      ),
    ).sort((left, right) => left.localeCompare(right));
    return ["All Staff", ...names];
  }, [items]);

  const isFilterChanged = useMemo(
    () =>
      studentsFilter !== DEFAULT_FILTERS.students ||
      gradeFilter !== DEFAULT_FILTERS.grade ||
      statusFilter !== DEFAULT_FILTERS.status ||
      assigneeFilter !== DEFAULT_FILTERS.assignee,
    [studentsFilter, gradeFilter, statusFilter, assigneeFilter],
  );

  const resetFilters = () => {
    setStudentsFilter(DEFAULT_FILTERS.students);
    setGradeFilter(DEFAULT_FILTERS.grade);
    setStatusFilter(DEFAULT_FILTERS.status);
    setAssigneeFilter(DEFAULT_FILTERS.assignee);
  };

  const [columnTitles, setColumnTitles] = useState<Record<string, string>>({
    Applied: "Applied",
    "In-Progress": "In-Progress",
    Scheduled: "Scheduled",
    Accepted: "Accepted",
    Enrolled: "Enrolled",
  });

  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [activeListPage, setActiveListPage] = useState(1);

  useEffect(() => {
    if (!familySelfSchedulingEnabled && sortKey === "interviewDate") {
      setSortKey("name");
      setSortDirection("asc");
    }
  }, [familySelfSchedulingEnabled, sortKey]);

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({
    name: 240,
    students: 100,
    siblings: 150,
    grade: 120,
    submitted: 150,
    status: 150,
    assignee: 180,
    interview: 200,
    actions: 140,
  });
  const enforceDesktopTableWidth = dashboardSurfaceWidth >= 1024;
  const listTableMinWidth = useMemo(
    () =>
      Object.entries(columnWidths).reduce((total, [columnId, width]) => {
        if (!familySelfSchedulingEnabled && columnId === "interview") {
          return total;
        }
        return total + width;
      }, 0),
    [columnWidths, familySelfSchedulingEnabled],
  );
  const [pendingOfferId, setPendingOfferId] = useState<string | null>(null);
  const [triggerApplicationQuery] =
    useLazyDomainRouterQuery<"getApplication">();
  const [addAdmissionsDocument] = useAddAdmissionsDocumentMutation();
  const [upsertEnrollmentStepData] = useUpsertEnrollmentStepDataMutation();
  const navigate = useNavigate();

  // Handle "Waitlisted" or "Archived" selection switching to List View
  useEffect(() => {
    if (statusFilter === "Waitlisted" || statusFilter === "Archived") {
      setViewMode("list");
    }
  }, [statusFilter]);

  // --- DnD Sensors ---
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 5 },
  });
  const keyboardSensor = useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates,
  });
  const sensors = useSensors(pointerSensor, keyboardSensor);

  useEffect(() => {
    if (!dragEnabled) {
      setActiveId(null);
    }
  }, [dragEnabled]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        actionMenuRef.current &&
        !actionMenuRef.current.contains(event.target as Node)
      ) {
        setActionMenuState(null);
      }
    };
    const closeActionMenu = () => setActionMenuState(null);
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("resize", closeActionMenu);
    window.addEventListener("scroll", closeActionMenu, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", closeActionMenu);
      window.removeEventListener("scroll", closeActionMenu, true);
    };
  }, []);

  // --- Handlers ---
  const handleApplicantClick = (applicant: Applicant) => {
    navigate(`/school/admissions/children/${applicant.id}`);
  };

  const handleScheduleClick = (applicant: Applicant, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!familySelfSchedulingEnabled) {
      if (typeof window !== "undefined") {
        try {
          window.sessionStorage.removeItem(ADMISSIONS_OPEN_SCHEDULE_KEY);
        } catch {
          // Ignore storage failures; disabled scheduling should remain a no-op.
        }
      }
      return;
    }
    if (typeof window !== "undefined") {
      try {
        window.sessionStorage.setItem(
          ADMISSIONS_OPEN_SCHEDULE_KEY,
          String(applicant.id ?? ""),
        );
      } catch {
        // Ignore storage failures and rely on route-state/query fallback.
      }
    }
    navigate(`/school/admissions/children/${applicant.id}?openSchedule=1`, {
      state: { openSchedule: true },
    });
  };

  const handleSendOffer = async (
    applicant: Applicant,
    e?: React.MouseEvent,
  ) => {
    if (e) e.stopPropagation();
    const applicationId = String(applicant.id || "").trim();
    if (!applicationId || pendingOfferId === applicationId) {
      return;
    }

    setPendingOfferId(applicationId);
    try {
      const applicationResponse = await triggerApplicationQuery(
        "getApplication",
        { id: applicationId },
      );
      const applicationRecord = asRecord(applicationResponse);
      const applicantRecord = asRecord(applicationRecord.applicant);
      const children = asArray<Record<string, unknown>>(
        applicationRecord.children,
      ).map((child) => asRecord(child));
      if (children.length === 0) {
        throw new Error("Application is missing child records.");
      }

      const normalizedApplicantCardName = readText(
        applicant.name,
      ).toLowerCase();
      const matchedChild = children.find((child) => {
        const childInfo = asRecord(child.childInfo);
        const childName =
          `${readText(childInfo.firstName)} ${readText(childInfo.lastName)}`
            .trim()
            .toLowerCase();
        return Boolean(childName) && childName === normalizedApplicantCardName;
      });
      const eligibleChild = children.find((child) => {
        const status = readText(child.status).toLowerCase();
        return (
          status === "accepted" ||
          status === "accepted_pending_enrollment" ||
          status === "enrolled"
        );
      });
      const targetChild = matchedChild ?? eligibleChild ?? children[0];
      const childInfo = asRecord(targetChild.childInfo);
      const childApplicationId = readText(targetChild.id);
      if (!childApplicationId) {
        throw new Error(
          "Unable to resolve child application id for acceptance letter generation.",
        );
      }

      const childName =
        `${readText(childInfo.firstName)} ${readText(childInfo.lastName)}`.trim() ||
        readText(applicant.name) ||
        "Student";
      const childFirstName =
        readText(childInfo.firstName).trim() ||
        childName.split(/\s+/).filter(Boolean)[0] ||
        "Student";
      const childStatus = readText(targetChild.status);
      const childGender = readText(childInfo.gender);
      const childDivision =
        readText(targetChild.division) || readText(childInfo.division);
      const familyName = readText(applicantRecord.familyName) || "Family";
      const schoolYear =
        readText(targetChild.schoolYear) ||
        readText(targetChild.academicYear) ||
        readText(applicationRecord.schoolYear) ||
        buildDefaultSchoolYear(
          asUnixSeconds(applicationRecord.submittedAt) ??
            asUnixSeconds(applicationRecord.createdAt),
        );
      const { childGenderNoun, childPronounSubject, childPronounPossessive } =
        resolveChildGenderWords(childGender);
      const templateVariant = resolveAcceptanceLetterTemplateVariant({
        childStatus,
        division: childDivision,
        gender: childGender,
      });
      const renderedLetter = renderAcceptanceLetter(templateVariant, {
        currentDate: formatLetterDate(),
        familyName,
        childName,
        childFirstName,
        registrationFeeDescription:
          schoolPublicInfo.registrationFee.descriptionLabel,
        schoolYear,
        schoolName: schoolPublicInfo.schoolName,
        schoolMission: schoolPublicInfo.schoolMissionStatement,
        schoolAddress: schoolPublicInfo.schoolAddress,
        schoolPhone: schoolPublicInfo.schoolPhoneDisplay,
        schoolEmail: schoolPublicInfo.schoolEmail,
        childGenderNoun,
        childPronounSubject,
        childPronounPossessive,
      });
      const pdfBytes = await buildAcceptanceLetterPdf({
        variant: templateVariant,
        title: renderedLetter.label,
        body: renderedLetter.content,
      });
      const pdfBuffer = Uint8Array.from(pdfBytes).buffer as ArrayBuffer;

      const codeToken = toSafeToken(
        readText(applicationRecord.code) || applicationId,
      );
      const childToken = toSafeToken(childName) || "student";
      const fileName = `${renderedLetter.fileStem}-${codeToken}-${childToken}.pdf`;
      const storagePath = buildAcceptanceLetterStoragePath({
        applicationId,
        childApplicationId,
        fileStem: renderedLetter.fileStem,
        childName,
      });
      const cognitoProfile = resolveCognitoProfile();
      const uploadedBy = readText(cognitoProfile?.userSub) || "system";
      await uploadData({
        path: storagePath,
        data: new Blob([pdfBuffer], { type: "application/pdf" }),
        options: {
          contentType: "application/pdf",
          metadata: {
            applicationId,
            childApplicationId,
            generatedBy: uploadedBy,
            templateVariant,
          },
        },
      }).result;

      const createdDocument = await addAdmissionsDocument({
        applicationId,
        document: {
          resourceId: storagePath,
          // Admissions domain currently restricts categories; keep acceptance semantics in fileName/content.
          category: "other",
          uploadedBy,
          fileName,
          contentType: "application/pdf",
          childApplicationIds: [childApplicationId],
          storageProvider: "s3",
          storageKey: storagePath,
          sizeBytes: pdfBytes.byteLength,
        },
      }).unwrap();

      const createdDocumentId = readText(asRecord(createdDocument).id);
      let linkedStepData = true;
      try {
        await upsertEnrollmentStepData({
          childApplicationId,
          step: "step1AcceptanceLetter",
          data: {
            reviewedAt: Math.floor(Date.now() / 1000),
            acknowledgedBy: "staff",
            notes: `${renderedLetter.label} generated by admissions on ${formatLetterDate()}.`,
            documentId: createdDocumentId || null,
            documentResourceId: storagePath,
          },
        }).unwrap();
      } catch {
        linkedStepData = false;
      }

      try {
        const signedUrl = await getUrl({ path: storagePath });
        window.open(signedUrl.url.toString(), "_blank", "noopener,noreferrer");
      } catch {
        // The document is still attached to the application even if preview launch fails.
      }

      setItems((previous) =>
        previous.map((item) =>
          item.id === applicationId
            ? {
                ...item,
                statusLabel: linkedStepData
                  ? "Acceptance letter linked"
                  : "Acceptance letter uploaded",
              }
            : item,
        ),
      );
    } catch (error) {
      console.error("[admissions] acceptance letter generation failed", {
        applicationId,
        error,
      });
      setItems((previous) =>
        previous.map((item) =>
          item.id === applicationId
            ? { ...item, statusLabel: "Letter generation failed" }
            : item,
        ),
      );
    } finally {
      setPendingOfferId(null);
    }
  };

  const handleQuickView = (applicant: Applicant) => {
    setQuickViewApplicant(applicant);
    setActionMenuState(null);
  };

  const handleFinancialAidAccessToggle = async (
    applicant: Applicant,
    enabled: boolean,
  ) => {
    const previousEnabled = Boolean(applicant.financialAidEnabled);
    setFinancialAidTogglePendingId(applicant.id);
    setFinancialAidToggleErrors((previous) => {
      const next = { ...previous };
      delete next[applicant.id];
      return next;
    });
    setItems((previous) =>
      previous.map((item) =>
        item.id === applicant.id
          ? { ...item, financialAidEnabled: enabled }
          : item,
      ),
    );

    try {
      const result = await unwrapMutationResult(
        updateApplicationMutation({
          applicationId: applicant.id,
          updates: { financialAidEnabled: enabled },
        }),
      );
      const resultRecord = asRecord(result);
      const persistedEnabled =
        typeof resultRecord.financialAidEnabled === "boolean"
          ? resultRecord.financialAidEnabled
          : enabled;
      setItems((previous) =>
        previous.map((item) =>
          item.id === applicant.id
            ? { ...item, financialAidEnabled: persistedEnabled }
            : item,
        ),
      );
    } catch (error) {
      setItems((previous) =>
        previous.map((item) =>
          item.id === applicant.id
            ? { ...item, financialAidEnabled: previousEnabled }
            : item,
        ),
      );
      setFinancialAidToggleErrors((previous) => ({
        ...previous,
        [applicant.id]: readErrorMessage(
          error,
          "Unable to update Financial Aid access.",
        ),
      }));
    } finally {
      setFinancialAidTogglePendingId((current) =>
        current === applicant.id ? null : current,
      );
    }
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const parseGrade = (gradeStr: string): number => {
    const val = gradeStr.replace("Grade ", "").trim();
    if (val === "K") return 0;
    const num = parseInt(val);
    return isNaN(num) ? 99 : num;
  };

  const handleArchiveAll = (status: string) => {
    setItems((prev) =>
      prev.map((a) => {
        return dashboardRawStatusMatches(a, status)
          ? { ...a, status: "Archived" }
          : a;
      }),
    );
  };

  const handleSortByDate = (status: string) => {
    setItems((prev) => {
      const matches = prev.filter((a) =>
        dashboardStatusMatches(a, status, familySelfSchedulingEnabled),
      );
      const nonMatches = prev.filter(
        (a) => !dashboardStatusMatches(a, status, familySelfSchedulingEnabled),
      );
      matches.sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
      );
      return [...nonMatches, ...matches];
    });
  };

  const handleSortByTitle = (status: string) => {
    setItems((prev) => {
      const matches = prev.filter((a) =>
        dashboardStatusMatches(a, status, familySelfSchedulingEnabled),
      );
      const nonMatches = prev.filter(
        (a) => !dashboardStatusMatches(a, status, familySelfSchedulingEnabled),
      );
      matches.sort((a, b) => a.name.localeCompare(b.name));
      return [...nonMatches, ...matches];
    });
  };

  const handleMoveAll = (
    currentStatus: string,
    newStatus: Applicant["status"],
  ) => {
    setItems((prev) =>
      prev.map((a) => {
        return dashboardRawStatusMatches(a, currentStatus)
          ? { ...a, status: newStatus }
          : a;
      }),
    );
  };

  const handleRenameColumn = (columnId: string, newTitle: string) => {
    setColumnTitles((prev) => ({ ...prev, [columnId]: newTitle }));
  };

  // --- Derived State ---
  const filteredAndSortedApplicants = useMemo(() => {
    let result = [...items];

    if (statusFilter === "Archived") {
      result = result.filter((a) => a.status === "Archived");
    } else {
      result = result.filter((a) => a.status !== "Archived");
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (a) =>
          a.name.toLowerCase().includes(query) ||
          a.studentNames?.some((name) => name.toLowerCase().includes(query)) ||
          a.applicationCode?.toLowerCase().includes(query) ||
          a.id.toLowerCase().includes(query),
      );
    }

    if (gradeFilter !== "All") {
      result = result.filter((a) => a.grade.includes(gradeFilter));
    }

    if (studentsFilter !== "All") {
      result = result.filter((applicant) => {
        const count = Number(applicant.studentsCount ?? 1);
        if (studentsFilter === "3+") return count >= 3;
        return String(count) === studentsFilter;
      });
    }

    if (statusFilter !== "All Status" && statusFilter !== "Archived") {
      result = result.filter(
        (a) =>
          dashboardStatusMatches(
            a,
            statusFilter,
            familySelfSchedulingEnabled,
          ) ||
          (familySelfSchedulingEnabled && a.statusLabel === statusFilter),
      );
    }

    if (assigneeFilter !== "All Staff") {
      result = result.filter((a) => a.assigneeName === assigneeFilter);
    }

    if (sortKey && (viewMode === "list" || statusFilter === "Archived")) {
      result.sort((a, b) => {
        let valA: any, valB: any;

        switch (sortKey) {
          case "name":
            valA = a.name.toLowerCase();
            valB = b.name.toLowerCase();
            break;
          case "studentsCount":
            valA = a.studentsCount || 1;
            valB = b.studentsCount || 1;
            break;
          case "siblings":
            valA = (a.siblings || []).join(", ").toLowerCase();
            valB = (b.siblings || []).join(", ").toLowerCase();
            break;
          case "grade":
            valA = parseGrade(a.grade);
            valB = parseGrade(b.grade);
            break;
          case "date":
            valA = new Date(a.date).getTime();
            valB = new Date(b.date).getTime();
            break;
          case "status":
            valA = normalizeDashboardStatus(
              a.status,
              familySelfSchedulingEnabled,
            ).toLowerCase();
            valB = normalizeDashboardStatus(
              b.status,
              familySelfSchedulingEnabled,
            ).toLowerCase();
            break;
          case "assigneeName":
            valA = (a.assigneeName || "").toLowerCase();
            valB = (b.assigneeName || "").toLowerCase();
            break;
          case "interviewDate":
            const statA = a.interviewDate ? 0 : 1;
            const statB = b.interviewDate ? 0 : 1;
            if (statA !== statB) {
              valA = statA;
              valB = statB;
            } else {
              valA = a.interviewDate ? new Date(a.interviewDate).getTime() : 0;
              valB = b.interviewDate ? new Date(b.interviewDate).getTime() : 0;
            }
            break;
          default:
            valA = "";
            valB = "";
        }

        if (valA < valB) return sortDirection === "asc" ? -1 : 1;
        if (valA > valB) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [
    items,
    searchQuery,
    studentsFilter,
    gradeFilter,
    statusFilter,
    assigneeFilter,
    familySelfSchedulingEnabled,
    sortKey,
    sortDirection,
    viewMode,
  ]);

  const listPageCount = Math.max(
    1,
    Math.ceil(filteredAndSortedApplicants.length / LIST_PAGE_SIZE),
  );
  const normalizedListPage = Math.min(activeListPage, listPageCount);
  const pagedApplicants = useMemo(() => {
    if (viewMode !== "list") return filteredAndSortedApplicants;
    const start = (normalizedListPage - 1) * LIST_PAGE_SIZE;
    return filteredAndSortedApplicants.slice(start, start + LIST_PAGE_SIZE);
  }, [
    LIST_PAGE_SIZE,
    filteredAndSortedApplicants,
    normalizedListPage,
    viewMode,
  ]);

  const visibleListPages = useMemo(() => {
    const maxButtons = 5;
    if (listPageCount <= maxButtons) {
      return Array.from({ length: listPageCount }, (_, index) => index + 1);
    }
    const start = Math.max(1, normalizedListPage - Math.floor(maxButtons / 2));
    const end = Math.min(listPageCount, start + maxButtons - 1);
    const adjustedStart = Math.max(1, end - maxButtons + 1);
    return Array.from(
      { length: end - adjustedStart + 1 },
      (_, index) => adjustedStart + index,
    );
  }, [listPageCount, normalizedListPage]);

  useEffect(() => {
    setActiveListPage(1);
  }, [
    searchQuery,
    studentsFilter,
    gradeFilter,
    statusFilter,
    assigneeFilter,
    viewMode,
    sortKey,
    sortDirection,
  ]);

  const startResizing = (colId: string, e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = columnWidths[colId];

    const onMouseMove = (moveEvent: MouseEvent) => {
      const currentWidth = startWidth + (moveEvent.clientX - startX);
      setColumnWidths((prev) => ({
        ...prev,
        [colId]: Math.max(50, currentWidth),
      }));
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  // --- DnD Logic ---

  const handleDragStart = (event: DragStartEvent) => {
    if (!dragEnabled) return;
    setActiveId(event.active.id as string);
    if (viewMode === "grid") {
      setSortKey(null);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    if (!dragEnabled) return;
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    if (activeId === overId) return;

    const isActiveApplicant = active.data.current?.type === "Applicant";
    const isOverApplicant = over.data.current?.type === "Applicant";

    if (!isActiveApplicant) return;

    let targetStatus: Applicant["status"] | undefined;
    let isOverColumn = false;

    if ((visibleKanbanStatuses as readonly string[]).includes(overId)) {
      targetStatus = overId as Applicant["status"];
      isOverColumn = true;
    } else if (isOverApplicant) {
      const overApplicant = items.find((a) => a.id === overId);
      if (overApplicant) {
        targetStatus = normalizeDashboardStatus(
          overApplicant.status,
          familySelfSchedulingEnabled,
        );
      }
    }

    if (!familySelfSchedulingEnabled && targetStatus === "Scheduled") {
      return;
    }

    const activeItemIndex = items.findIndex((a) => a.id === activeId);
    const overItemIndex = items.findIndex((a) => a.id === overId);

    if (isOverColumn && targetStatus) {
      setItems((prev) => {
        const activeItem = prev[activeItemIndex];
        if (activeItem.status !== targetStatus) {
          const newItems = [...prev];
          newItems[activeItemIndex] = { ...activeItem, status: targetStatus! };
          return newItems;
        }
        return prev;
      });
      return;
    }

    if (isOverApplicant && targetStatus) {
      setItems((prev) => {
        const activeItem = prev[activeItemIndex];
        const newItems = [...prev];
        const isStatusChange = activeItem.status !== targetStatus;

        if (isStatusChange) {
          newItems[activeItemIndex] = { ...activeItem, status: targetStatus! };
        }
        return arrayMove(newItems, activeItemIndex, overItemIndex);
      });
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    if (!dragEnabled) return;

    const { active, over } = event;

    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    if (activeId === overId) return;

    const activeIndex = items.findIndex((a) => a.id === activeId);
    const overIndex = items.findIndex((a) => a.id === overId);

    if (activeIndex !== -1 && overIndex !== -1) {
      setItems((prev) => arrayMove(prev, activeIndex, overIndex));
    }
  };

  const dropAnimation: DropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: "0.5",
        },
      },
    }),
  };

  const getColumnApplicants = (statusGroup: string) => {
    return filteredAndSortedApplicants.filter((a) =>
      dashboardStatusMatches(a, statusGroup, familySelfSchedulingEnabled),
    );
  };

  const activeApplicant = useMemo(
    () => (dragEnabled ? items.find((a) => a.id === activeId) : null),
    [activeId, dragEnabled, items],
  );

  const filtersContent = (
    <FilterBarSection mobile={isMobile}>
      <FilterDropdownField
        label="Students"
        value={studentsFilter}
        options={toSelectOptions(["All", "1", "2", "3+"])}
        onChange={setStudentsFilter}
        mobile={isMobile}
        className={
          isMobile
            ? "w-full rounded-xl border border-gray-100 bg-white"
            : "border-r border-gray-100 pr-1"
        }
        placeholder="Students"
        testId="admissions-dashboard-filter-students"
      />
      <FilterDropdownField
        label="Grade"
        value={gradeFilter}
        options={toSelectOptions([
          "All",
          "K",
          "1",
          "2",
          "3",
          "4",
          "5",
          "6",
          "7",
          "8",
        ])}
        onChange={setGradeFilter}
        mobile={isMobile}
        className={
          isMobile
            ? "w-full rounded-xl border border-gray-100 bg-white"
            : "border-r border-gray-100 pr-1"
        }
        placeholder="All Grades"
        testId="admissions-dashboard-filter-grade"
      />
      <FilterDropdownField
        label="Status"
        value={statusFilter}
        options={toSelectOptions([
          "All Status",
          "Applied",
          "In-Progress",
          ...(familySelfSchedulingEnabled ? ["Scheduled"] : []),
          "Accepted",
          "Enrolled",
          "Denied",
          "Waitlisted",
          "Archived",
        ])}
        onChange={setStatusFilter}
        mobile={isMobile}
        className={
          isMobile
            ? "w-full rounded-xl border border-gray-100 bg-white"
            : "border-r border-gray-100 pr-1"
        }
        placeholder="All Status"
        testId="admissions-dashboard-filter-status"
      />
      <FilterDropdownField
        label="Assignee"
        value={assigneeFilter}
        options={toSelectOptions(assigneeOptions)}
        onChange={setAssigneeFilter}
        mobile={isMobile}
        className={
          isMobile
            ? "w-full rounded-xl border border-gray-100 bg-white"
            : "pr-1"
        }
        placeholder="All Staff"
        testId="admissions-dashboard-filter-assignee"
      />
      {isFilterChanged ? (
        <button
          type="button"
          onClick={resetFilters}
          className={`${isMobile ? "col-span-2 w-full flex justify-center py-2 text-xs" : "ml-2 mr-3 p-1"} rounded-lg border border-gray-100 bg-gray-50 text-irongrey/40 transition-all cursor-pointer hover:text-strawberry`}
          title="Reset Filters"
        >
          {isMobile ? "Reset All Filters" : <X size={14} />}
        </button>
      ) : null}
    </FilterBarSection>
  );

  return (
    <section
      id="main"
      ref={dashboardRootRef}
      data-testid="admissions-dashboard-root"
      data-route-identity="school-admissions-dashboard"
      data-route-owner="internal-owned"
      className="flex h-full min-h-0 flex-col overflow-hidden"
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={dragEnabled ? handleDragStart : undefined}
        onDragOver={dragEnabled ? handleDragOver : undefined}
        onDragEnd={dragEnabled ? handleDragEnd : undefined}
      >
        <AddApplicantModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
        />
        <QuickViewPanel
          applicant={quickViewApplicant}
          familySelfSchedulingEnabled={familySelfSchedulingEnabled}
          onClose={() => setQuickViewApplicant(null)}
          onViewFull={() => {
            if (!quickViewApplicant) return;
            setQuickViewApplicant(null);
            navigate(`/school/admissions/children/${quickViewApplicant.id}`);
          }}
        />

        <header className="bg-background-light border-b border-gray-100 pb-4 flex-shrink-0 flex flex-col gap-4">
          <div className="h-[50px] flex items-start lg:items-center justify-between gap-4 w-full">
            <div className="flex min-w-0 flex-1 items-center gap-4 overflow-hidden">
              <SearchFilterControl
                inputTestId="admissions-dashboard-search-input"
                toggleTestId="admissions-dashboard-filter-toggle-button"
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search..."
                toggleActive={!isMobile && showFilters}
                onToggleFilters={() => setShowFilters(!showFilters)}
                className="w-full max-w-[320px] min-w-0"
              />

              {!isMobile ? (
                <aside
                  aria-hidden={!showFilters}
                  className={`flex items-center rounded-2xl border border-gray-100 bg-white p-1 shadow-sm max-w-full transition-all ease-in-out ${showFilters ? "duration-300 opacity-100 translate-x-0" : "duration-150 max-w-0 opacity-0 -translate-x-4 overflow-hidden pointer-events-none"}`}
                >
                  {filtersContent}
                </aside>
              ) : null}
            </div>

            <nav
              className="flex shrink-0 items-center gap-3 self-start lg:self-center"
              aria-label="Admissions dashboard view controls"
            >
              <div className="bg-white p-1 rounded-lg flex shadow-sm border border-gray-100">
                <button
                  type="button"
                  aria-label="Grid view"
                  onClick={() => {
                    if (
                      statusFilter !== "Waitlisted" &&
                      statusFilter !== "Archived"
                    ) {
                      setViewMode("grid");
                      setSortKey(null);
                    }
                  }}
                  disabled={
                    statusFilter === "Waitlisted" || statusFilter === "Archived"
                  }
                  data-testid="admissions-dashboard-view-grid"
                  data-test-id="dashboard-view-board"
                  className={`p-2 px-3 rounded-md cursor-pointer transition-all ${viewMode === "grid" ? "bg-primary/10 text-primary shadow-sm" : "text-irongrey/40 hover:bg-gray-50"} ${statusFilter === "Waitlisted" || statusFilter === "Archived" ? "opacity-30 cursor-not-allowed" : ""}`}
                >
                  <LayoutGrid
                    size={20}
                    className={viewMode === "grid" ? "fill-current" : ""}
                  />
                </button>
                <button
                  type="button"
                  aria-label="List view"
                  onClick={() => {
                    setViewMode("list");
                    setSortKey("name");
                  }}
                  data-testid="admissions-dashboard-view-list"
                  data-test-id="dashboard-view-list"
                  className={`p-2 px-3 rounded-md cursor-pointer transition-all ${viewMode === "list" ? "bg-primary/10 text-primary shadow-sm" : "text-irongrey/40 hover:bg-gray-50"}`}
                >
                  <ListIcon
                    size={20}
                    className={viewMode === "list" ? "fill-current" : ""}
                  />
                </button>
              </div>
              <button
                type="button"
                onClick={() => setIsAddModalOpen(true)}
                className="bg-primary hover:bg-primary-hover text-white p-2 rounded-lg shadow-lg shadow-primary/20 transition-all cursor-pointer"
              >
                <Plus size={24} />
              </button>
            </nav>
          </div>

          {/* Mobile Filters Section */}
          {isMobile && showFilters && (
            <section className="animate-in slide-in-from-top-2 duration-200">
              {filtersContent}
            </section>
          )}
        </header>

        <main className="flex-1 min-h-0 overflow-hidden pt-2 pb-0 mb-6">
          {isInitialLoading ? (
            <section
              data-testid="admissions-dashboard-loading-skeleton"
              className="h-full min-h-0"
              aria-label="Admissions dashboard loading"
              aria-busy="true"
            >
              {viewMode === "grid" ? (
                <AdmissionsBoardSkeleton statuses={visibleKanbanStatuses} />
              ) : (
                <AdmissionsListSkeleton />
              )}
            </section>
          ) : hasInitialLoadError ? (
            <section
              className="flex h-full min-h-0 items-center justify-center"
              aria-live="polite"
            >
              <SupportErrorMessage className="max-w-xl">
                Unable to load applications right now.
              </SupportErrorMessage>
            </section>
          ) : viewMode === "grid" ? (
            <section
              data-testid="admissions-dashboard-board-root"
              className="h-full min-h-0 overflow-auto pb-10"
              aria-label="Admissions board view"
            >
              <section
                data-testid="admissions-dashboard-board-scroll"
                className="min-w-full"
              >
                <div
                  data-testid="admissions-dashboard-kanban-grid"
                  className="grid gap-2 items-start"
                  style={{ gridTemplateColumns: KANBAN_GRID_TEMPLATE }}
                >
                  {visibleKanbanStatuses.map((status) => (
                    <KanbanColumn
                      key={status}
                      id={status}
                      title={columnTitles[status]}
                      color={getBoardStatusColor(status)}
                      count={getColumnApplicants(status).length}
                      applicants={getColumnApplicants(status)}
                      onCardClick={handleApplicantClick}
                      onAddApplicant={() => setIsAddModalOpen(true)}
                      onArchiveAll={() => handleArchiveAll(status)}
                      onSortByDate={() => handleSortByDate(status)}
                      onMoveAll={(newStat) => handleMoveAll(status, newStat)}
                      onRenameTitle={(newTitle) =>
                        handleRenameColumn(status, newTitle)
                      }
                      onSendOffer={handleSendOffer}
                      sendingOfferId={pendingOfferId}
                      dragEnabled={dragEnabled}
                      familySelfSchedulingEnabled={familySelfSchedulingEnabled}
                    />
                  ))}
                </div>
              </section>
            </section>
          ) : (
            <section
              className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden h-full min-h-0 flex flex-col"
              aria-label="Admissions list view"
            >
              <div className="overflow-auto flex-1 pb-24">
                <table
                  className="table-sticky-last-action-col w-full text-left border-collapse table-auto"
                  style={
                    enforceDesktopTableWidth
                      ? { minWidth: `${Math.floor(listTableMinWidth)}px` }
                      : undefined
                  }
                >
                  <thead className="sticky top-0 bg-gray-50">
                    <tr className="border-b border-gray-100 bg-gray-50 shadow-[0_1px_0_0_rgba(229,231,235,1)]">
                      <HeaderCell
                        id="name"
                        width={columnWidths.name}
                        onResize={startResizing}
                        onClick={() => handleSort("name")}
                        label="Family Name"
                      />
                      <HeaderCell
                        id="students"
                        width={columnWidths.students}
                        onResize={startResizing}
                        onClick={() => handleSort("studentsCount")}
                        label="Students"
                        center
                      />
                      <HeaderCell
                        id="siblings"
                        width={columnWidths.siblings}
                        onResize={startResizing}
                        onClick={() => handleSort("siblings")}
                        label="Siblings"
                      />
                      <HeaderCell
                        id="grade"
                        width={columnWidths.grade}
                        onResize={startResizing}
                        onClick={() => handleSort("grade")}
                        label="Grades"
                      />
                      <HeaderCell
                        id="submitted"
                        width={columnWidths.submitted}
                        onResize={startResizing}
                        onClick={() => handleSort("date")}
                        label="Submitted"
                      />
                      <HeaderCell
                        id="status"
                        width={columnWidths.status}
                        onResize={startResizing}
                        onClick={() => handleSort("status")}
                        label="Status"
                      />
                      <HeaderCell
                        id="assignee"
                        width={columnWidths.assignee}
                        onResize={startResizing}
                        onClick={() => handleSort("assigneeName")}
                        label="Assignee"
                      />
                      {familySelfSchedulingEnabled ? (
                        <HeaderCell
                          id="interview"
                          width={columnWidths.interview}
                          onResize={startResizing}
                          onClick={() => handleSort("interviewDate")}
                          label="Interview"
                        />
                      ) : null}
                      <HeaderCell
                        id="actions"
                        width={columnWidths.actions}
                        onResize={startResizing}
                        label="Actions"
                        right
                      />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {pagedApplicants.map((applicant) => (
                      <tr
                        key={applicant.id}
                        data-testid="admissions-dashboard-applicant-row"
                        onClick={(event) => {
                          const target = event.target as HTMLElement | null;
                          if (
                            target?.closest(
                              'button, a, input, select, textarea, [role="button"]',
                            )
                          ) {
                            return;
                          }
                          handleApplicantClick(applicant);
                        }}
                        className="hover:bg-gray-50 transition-colors group cursor-pointer h-16 border-b border-gray-200"
                      >
                        <td
                          className="px-2 py-4 overflow-hidden"
                          style={{ width: columnWidths.name }}
                        >
                          <div className="flex items-center gap-3 overflow-hidden whitespace-nowrap text-ellipsis">
                            {applicant.avatar ? (
                              <img
                                alt={applicant.name}
                                className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                                src={applicant.avatar}
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs flex-shrink-0">
                                {getInitials(applicant.name)}
                              </div>
                            )}
                            <div className="overflow-hidden">
                              <p
                                className="font-bold text-sm text-irongrey truncate"
                                title={applicant.name}
                              >
                                {applicant.name}
                              </p>
                              <p className="text-xs text-irongrey/40 truncate">
                                ID: #{applicant.id}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td
                          className="px-2 py-4 text-center overflow-hidden whitespace-nowrap text-ellipsis"
                          style={{ width: columnWidths.students }}
                        >
                          <span className="bg-gray-100 text-irongrey px-2 py-0.5 rounded text-xs font-bold">
                            {applicant.studentsCount || 1}
                          </span>
                        </td>
                        <td
                          className="px-2 py-4 overflow-hidden whitespace-nowrap text-ellipsis"
                          style={{ width: columnWidths.siblings }}
                        >
                          <span
                            className="text-xs font-medium text-irongrey/70 truncate"
                            title={applicant.siblings?.join(", ")}
                          >
                            {applicant.siblings
                              ? applicant.siblings.join(", ")
                              : "-"}
                          </span>
                        </td>
                        <td
                          className="px-2 py-4 overflow-hidden whitespace-nowrap text-ellipsis"
                          style={{ width: columnWidths.grade }}
                        >
                          <span className="text-xs font-medium text-irongrey truncate">
                            {applicant.grade}
                          </span>
                        </td>
                        <td
                          className="px-2 py-4 text-xs text-irongrey/60 overflow-hidden whitespace-nowrap text-ellipsis"
                          style={{ width: columnWidths.submitted }}
                        >
                          {applicant.date}
                        </td>
                        <td
                          className="px-2 py-4 overflow-hidden whitespace-nowrap text-ellipsis"
                          style={{ width: columnWidths.status }}
                        >
                          <StatusBadge
                            status={applicant.status}
                            familySelfSchedulingEnabled={
                              familySelfSchedulingEnabled
                            }
                          />
                        </td>
                        <td
                          className="px-2 py-4 overflow-hidden whitespace-nowrap text-ellipsis"
                          style={{ width: columnWidths.assignee }}
                        >
                          <div className="flex items-center gap-2 overflow-hidden">
                            {applicant.assigneeAvatar ? (
                              <img
                                alt={applicant.assigneeName}
                                className="w-5 h-5 rounded-full flex-shrink-0"
                                src={applicant.assigneeAvatar}
                              />
                            ) : (
                              <div className="w-5 h-5 rounded-full bg-gray-100 flex-shrink-0" />
                            )}
                            <span className="text-xs font-medium truncate">
                              {applicant.assigneeName || "-"}
                            </span>
                          </div>
                        </td>
                        {familySelfSchedulingEnabled ? (
                          <td
                            className="px-2 py-4 overflow-hidden whitespace-nowrap text-ellipsis"
                            style={{ width: columnWidths.interview }}
                          >
                            {applicant.interviewDate ? (
                              <span className="text-xs font-bold text-vibrant-coral truncate">
                                {applicant.interviewDate}
                              </span>
                            ) : applicant.status === "Accepted" ||
                              applicant.status === "Enrolled" ||
                              applicant.status === "Denied" ? (
                              <span className="text-xs font-medium text-irongrey/40">
                                Completed
                              </span>
                            ) : applicant.status === "Archived" ? (
                              <span className="text-xs font-medium text-irongrey/40">
                                -
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={(e) =>
                                  handleScheduleClick(applicant, e)
                                }
                                className="text-xs font-bold text-primary border border-primary/30 px-3 py-1 rounded-lg hover:bg-primary hover:text-white transition-all whitespace-nowrap cursor-pointer"
                              >
                                Schedule Event
                              </button>
                            )}
                          </td>
                        ) : null}
                        <td
                          className="px-2 py-4 text-right overflow-visible"
                          style={{ width: columnWidths.actions }}
                        >
                          <div>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (actionMenuState?.id === applicant.id) {
                                  setActionMenuState(null);
                                  return;
                                }
                                const rect =
                                  e.currentTarget.getBoundingClientRect();
                                setActionMenuState({
                                  id: applicant.id,
                                  top: rect.bottom + window.scrollY + 4,
                                  left: rect.right + window.scrollX,
                                });
                              }}
                              className="text-irongrey/40 hover:text-primary p-2 -mr-2 rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                            >
                              <MoreHorizontal size={20} />
                            </button>

                            {actionMenuState?.id === applicant.id &&
                            typeof document !== "undefined"
                              ? createPortal(
                                  <div
                                    ref={actionMenuRef}
                                    className="absolute w-48 rounded-xl border border-gray-100 bg-white py-1.5 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)] z-[9999] animate-in fade-in zoom-in-95 duration-100 origin-top-right"
                                    style={{
                                      top: actionMenuState.top,
                                      left: actionMenuState.left,
                                      transform: "translateX(-100%)",
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <label
                                      className="flex w-full cursor-pointer items-center justify-between gap-3 px-4 py-2.5 text-xs font-bold text-irongrey transition-colors hover:bg-gray-50"
                                      data-testid={`admissions-dashboard-financial-aid-action-${applicant.id}`}
                                    >
                                      <span className="flex items-center gap-3">
                                        <DollarSign
                                          size={14}
                                          className="text-primary"
                                        />
                                        Financial Aid
                                      </span>
                                      <input
                                        type="checkbox"
                                        className="sr-only"
                                        checked={Boolean(
                                          applicant.financialAidEnabled,
                                        )}
                                        disabled={
                                          financialAidTogglePendingId ===
                                          applicant.id
                                        }
                                        aria-label={`Financial Aid access for ${applicant.name}`}
                                        data-testid={`admissions-dashboard-financial-aid-toggle-${applicant.id}`}
                                        onChange={(event) => {
                                          void handleFinancialAidAccessToggle(
                                            applicant,
                                            event.target.checked,
                                          );
                                        }}
                                      />
                                      <span
                                        aria-hidden="true"
                                        className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors ${
                                          applicant.financialAidEnabled
                                            ? "bg-primary"
                                            : "bg-gray-200"
                                        } ${financialAidTogglePendingId === applicant.id ? "opacity-60" : ""}`}
                                      >
                                        <span
                                          className={`absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                                            applicant.financialAidEnabled
                                              ? "translate-x-4"
                                              : "translate-x-0"
                                          }`}
                                        />
                                      </span>
                                    </label>
                                    {financialAidTogglePendingId ===
                                    applicant.id ? (
                                      <p className="px-4 pb-2 text-[11px] font-semibold text-irongrey/45">
                                        Saving Financial Aid access…
                                      </p>
                                    ) : null}
                                    {financialAidToggleErrors[applicant.id] ? (
                                      <p
                                        className="px-4 pb-2 text-[11px] font-semibold text-strawberry"
                                        data-testid={`admissions-dashboard-financial-aid-error-${applicant.id}`}
                                      >
                                        {financialAidToggleErrors[applicant.id]}
                                      </p>
                                    ) : null}
                                    <div className="my-1 border-t border-gray-100" />
                                    <button
                                      type="button"
                                      onClick={() => handleQuickView(applicant)}
                                      className="w-full text-left px-4 py-2.5 text-xs font-bold text-irongrey hover:bg-gray-50 flex items-center gap-3 transition-colors"
                                    >
                                      <Eye
                                        size={14}
                                        className="text-irongrey/60"
                                      />
                                      Quick view
                                    </button>
                                  </div>,
                                  document.body,
                                )
                              : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </main>

        {viewMode === "list" && listPageCount > 1 ? (
          <nav
            className="px-8 py-4 flex items-center justify-center gap-6 mt-auto"
            aria-label="Admissions list pagination"
          >
            <button
              type="button"
              onClick={() => setActiveListPage((prev) => Math.max(1, prev - 1))}
              disabled={normalizedListPage <= 1}
              className="text-irongrey/30 hover:text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Previous page"
            >
              <ChevronDown size={20} className="rotate-90" />
            </button>
            <div className="flex items-center gap-4">
              {visibleListPages.map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={() => setActiveListPage(page)}
                  aria-current={
                    normalizedListPage === page ? "page" : undefined
                  }
                  className={`w-10 h-10 rounded-xl font-bold transition-colors flex items-center justify-center text-[0.7rem] ${
                    normalizedListPage === page
                      ? "bg-primary text-white shadow-sm shadow-primary/20"
                      : "text-irongrey hover:bg-gray-100"
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() =>
                setActiveListPage((prev) => Math.min(listPageCount, prev + 1))
              }
              disabled={normalizedListPage >= listPageCount}
              className="text-irongrey/30 hover:text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Next page"
            >
              <ChevronDown size={20} className="-rotate-90" />
            </button>
          </nav>
        ) : null}

        <DragOverlay dropAnimation={dropAnimation}>
          {activeApplicant ? (
            <div
              data-testid="admissions-dashboard-drag-overlay"
              className="transform scale-105 opacity-90 cursor-grabbing"
            >
              <SortableApplicantCard
                applicant={activeApplicant}
                onClick={handleApplicantClick}
                onSendOffer={handleSendOffer}
                isSendingOffer={false}
                clickable={false}
                dragEnabled={false}
                familySelfSchedulingEnabled={familySelfSchedulingEnabled}
              />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </section>
  );
};
